import { useEffect, useState, useRef } from "react";
import { api, API } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { toast } from "sonner";
import { Camera, X, Upload, Loader2, ImagePlus } from "lucide-react";

const Lightbox = ({ photo, onClose }) => (
  <div
    data-testid="gallery-lightbox"
    onClick={onClose}
    className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
  >
    <button
      onClick={onClose}
      data-testid="lightbox-close"
      className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
    >
      <X className="w-7 h-7" />
    </button>
    <img
      src={`${API}/photos/${photo.id}/image`}
      alt={photo.name || "Foto"}
      className="max-h-[85vh] max-w-full rounded-md object-contain"
      onClick={(e) => e.stopPropagation()}
    />
  </div>
);

export const Gallery = () => {
  const { t } = useLang();
  const [photos, setPhotos] = useState([]);
  const [active, setActive] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!file) return;
    setSending(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", name);
    try {
      await api.post("/photos/customer", fd);
      toast.success(t("photoThanks"));
      setShowUpload(false);
      setFile(null);
      setName("");
    } catch (e) {
      toast.error(t("photoError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="gallery-section" className="px-6 mt-12">
      <div className="gd-divider mb-4" aria-hidden="true">
        <Camera className="w-4 h-4" />
      </div>
      <div className="text-center mb-5">
        <h3 className="font-serif text-3xl text-[#d4af37]">
          {t("galleryTitle")}
        </h3>
        <p className="text-xs text-[#e0ddcf]/70 mt-1">{t("gallerySubtitle")}</p>
      </div>

      {photos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2" data-testid="gallery-grid">
          {photos.map((p) => (
            <button
              key={p.id}
              data-testid={`gallery-photo-${p.id}`}
              onClick={() => setActive(p)}
              className="relative aspect-square overflow-hidden rounded-sm border border-[#d4af37]/20 group"
            >
              <img
                src={`${API}/photos/${p.id}/image`}
                alt={p.name || "Foto galleria"}
                loading="lazy"
                className="w-full h-full object-cover group-active:scale-105 transition-transform duration-300"
              />
              {p.name && (
                <span className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-[9px] text-white/90 px-1 pb-0.5 pt-3 truncate text-left">
                  {p.name}
                </span>
              )}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-[#e0ddcf]/50 py-6">
          {t("galleryEmpty")}
        </p>
      )}

      <button
        onClick={() => setShowUpload(true)}
        data-testid="open-share-photo-button"
        className="mt-5 w-full bg-transparent border border-[#d4af37] text-[#d4af37] px-6 py-3 rounded-sm hover:bg-[#d4af37]/10 active:bg-[#d4af37]/20 transition-colors flex items-center justify-center gap-2 font-medium"
      >
        <ImagePlus className="w-4 h-4" /> {t("sharePhoto")}
      </button>

      {active && <Lightbox photo={active} onClose={() => setActive(null)} />}

      {showUpload && (
        <div
          data-testid="share-photo-modal"
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !sending && setShowUpload(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0f172a] border border-[#d4af37]/30 rounded-md p-6 gd-fade-up"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-serif text-2xl text-[#d4af37]">
                {t("sharePhoto")}
              </h4>
              <button
                onClick={() => !sending && setShowUpload(false)}
                data-testid="close-share-modal"
                className="text-[#e0ddcf]/60 hover:text-[#e0ddcf]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("yourName")}
              data-testid="photo-name-input"
              className="w-full bg-[#0b1120] border border-[#d4af37]/30 rounded-sm px-3 py-2.5 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37] mb-3"
            />

            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="photo-file-input"
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
            />
            <button
              onClick={() => fileRef.current?.click()}
              className="w-full border border-dashed border-[#d4af37]/40 rounded-sm px-4 py-6 text-center text-sm text-[#e0ddcf]/80 hover:border-[#d4af37] transition-colors flex flex-col items-center gap-2"
            >
              <Camera className="w-6 h-6 text-[#d4af37]" />
              {file ? file.name : t("choosePhoto")}
            </button>

            <div className="flex gap-3 mt-5">
              <button
                onClick={() => !sending && setShowUpload(false)}
                className="flex-1 bg-transparent border border-[#d4af37]/40 text-[#e0ddcf] px-4 py-2.5 rounded-sm text-sm"
              >
                {t("cancel")}
              </button>
              <button
                onClick={submit}
                disabled={!file || sending}
                data-testid="submit-photo-button"
                className="flex-1 bg-[#d4af37] text-[#060a14] font-semibold px-4 py-2.5 rounded-sm active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-50 text-sm"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4" /> {t("send")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
